import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrypto } from '../contexts/CryptoContext';
import SafeheirLogo from '../components/SafeheirLogo';

const MAX_ATTEMPTS = 5;
const LOCK_DURATION = 30; // seconds

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockCountdown, setLockCountdown] = useState(0);
  const lockTimer = useRef(null);

  const { login } = useAuth();
  const { unlockVault } = useCrypto();
  const navigate = useNavigate();

  useEffect(() => {
    return () => { if (lockTimer.current) clearInterval(lockTimer.current); };
  }, []);

  const startLockdown = () => {
    setLockCountdown(LOCK_DURATION);
    lockTimer.current = setInterval(() => {
      setLockCountdown(prev => {
        if (prev <= 1) {
          clearInterval(lockTimer.current);
          lockTimer.current = null;
          setFailedAttempts(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const isLocked = lockCountdown > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) return;
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      // Non-bloquant : si unlockVault échoue, UnlockVaultModal prend le relai
      if (result.userId) unlockVault(password, result.userId).catch(() => {});
      navigate('/dashboard');
    } else {
      const next = failedAttempts + 1;
      setFailedAttempts(next);
      if (next >= MAX_ATTEMPTS) {
        setError(`Trop de tentatives échouées. Veuillez patienter ${LOCK_DURATION} secondes.`);
        startLockdown();
      } else {
        setError(`${result.error || 'Identifiants incorrects'} (${next}/${MAX_ATTEMPTS} tentatives)`);
      }
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    color: '#fff',
    fontSize: 14,
    outline: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0A1628',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <SafeheirLogo size="lg" />
          </Link>
          <h1 style={{ margin: '1.5rem 0 0.4rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>Connexion</h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem' }}>Accédez à votre espace sécurisé</p>
        </div>

        <div style={{
          background: 'rgba(10,22,40,0.7)',
          backdropFilter: 'blur(20px)',
          borderRadius: 16,
          padding: '2rem',
          border: '1px solid rgba(255,255,255,0.07)',
        }}>
          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 20,
              color: '#f87171',
              fontSize: 14,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                Adresse email
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>
                Mot de passe
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={inputStyle}
              />
            </div>

            <button
              type="submit"
              disabled={loading || isLocked}
              style={{
                marginTop: 4,
                width: '100%',
                padding: '11px 0',
                background: isLocked ? 'rgba(239,68,68,0.18)' : '#00F5A0',
                color: isLocked ? '#f87171' : '#0A1628',
                border: isLocked ? '1px solid rgba(239,68,68,0.35)' : 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 14,
                cursor: (loading || isLocked) ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'background 0.3s, color 0.3s',
              }}
            >
              {isLocked
                ? `Réessayer dans ${lockCountdown}s`
                : loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>

        <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Link to="/register" style={{ color: '#00F5A0', textDecoration: 'none', fontSize: '0.875rem' }}>
            Créer un compte
          </Link>
          <Link to="/" style={{ color: 'rgba(255,255,255,0.35)', textDecoration: 'none', fontSize: '0.875rem' }}>
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
